// src/pages/B2B/ProductInfo/ProductInfoPageB2B.jsx
import React, {
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MLink,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  useMediaQuery,
} from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

import ProductSideBarConfigB2B from "../../../ProductSidebarConfigB2B";
import {
  normalizeHref,
  findProductInfoRoot,
  findNodeByHref,
  getAncestors,
} from "../ProductSidebar";

import ProductCard from "../../../GlobalFunction/ProductCard";
import SehoLoadingSpinner from "../../../GlobalFunction/SehoLoadingSpinner";
import { ProductsAPI } from "../../../../api/Product";
import { resolveAssetUrl } from "../../../../lib/http";

import ProductIntroTopSection from "./ProductIntroTopSection";
import ProductConsultForm from "./ProductConsultForm";
import { normalizeProductForCard } from "../Normalizers";
import { useAuth } from "../../Auth/AuthContext";
import { resolveCanonicalUserID } from "../../../../Utils/UserIdentity";
import ServiceProcess from "./ServiceProcess";
import { getLangFromCookie, LANG_COOKIE } from "../../../../Utils/LangCookie";
import ServiceAdvantages from "./ServiceAdvantages";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import ArrowButton from "../../../GlobalFunction/ArrowButton";
import {
  getB2BCategoryPreviewImage,
  getB2BGroupPreviewImage,
} from "./CategoryPreviewImages";
import CategoryGroup from "./CategoryGroup";

/* ---------------- language helpers & UI text ---------------- */

const font = {
  ZH: "LINE Seed TW",
  EN: "LINE Seed Sans",
  JP: "LINE Seed JP",
};

function normalizeId(v) {
  const s = String(v ?? "").trim();
  return s ? s : "";
}

function getParentKeyFromProduct(p) {
  return (
    normalizeId(p?.parentID) ||
    normalizeId(p?.parentId) ||
    normalizeId(p?.parent_id) ||
    normalizeId(p?.parent_product_id) ||
    normalizeId(p?.productID) ||
    normalizeId(p?.productId) ||
    normalizeId(p?.id) ||
    normalizeId(p?.main?.["編號"]) ||
    normalizeId(p?.main?.productID) ||
    normalizeId(p?.main?.id)
  );
}

function getVariantKeyFromProduct(p) {
  return (
    normalizeId(p?.productID) ||
    normalizeId(p?.productId) ||
    normalizeId(p?.id) ||
    normalizeId(p?.main?.["編號"]) ||
    normalizeId(p?.main?.id)
  );
}

function buildB2BDetailPath(prod) {
  const parentKey = getParentKeyFromProduct(prod);
  const variantKey = getVariantKeyFromProduct(prod);

  if (!parentKey) return "";

  let url = `/商品資訊/${encodeURIComponent(parentKey)}`;

  if (variantKey && variantKey !== parentKey) {
    url += `?variant=${encodeURIComponent(variantKey)}`;
  }

  return url;
}

const normalizeLang = (code) => (code === "EN" || code === "JP" ? code : "ZH");

const getCurrentLang = () =>
  normalizeLang(
    getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH",
  );

const byLangNode = (node = {}, lang = "EN") => {
  if (lang === "EN") return node.EN || node.label;
  if (lang === "JP") return node.JP || node.label;
  return node.label;
};

const UI_TEXT = {
  ZH: {
    backChip: "上一層",
    searchPlaceholder: "輸入關鍵字即時篩選商品…",
    clear: "清除",
    loading: "讀取中...",
    loadError: "載入商品失敗",
    noProducts: "目前沒有符合的商品。",
    pageTitleFallback: "合作資訊",
    perPageLabel: (n) => `每頁 ${n}`,
    pageCaption: (page, totalPages, pageSize, max) =>
      `第 ${page} / 共 ${totalPages} 頁（每頁 ${pageSize} 件， 最多 ${max} 件）`,
    consultTitle: "專業諮詢",
    consultCta: "立即諮詢專人服務",
    consultCtaMB: "專人服務",
    consultBtnTop: { xs: "50%", md: "50%" },
    consultBtnleft: { xs: "17%", md: "22%" },
    brandLabel: "品牌",
    brandAll: "全部品牌",
  },
  EN: {
    backChip: "Back",
    searchPlaceholder: "Type keywords to filter products…",
    clear: "Clear",
    loading: "Loading...",
    loadError: "Failed to load products",
    noProducts: "No matching products at the moment.",
    pageTitleFallback: "Product Information",
    perPageLabel: (n) => `${n} items`,
    pageCaption: (page, totalPages, pageSize, max) =>
      `Page ${page} / ${totalPages} ( ${pageSize} items per page )`,
    consultTitle: "Consult",
    consultCta: "Contact support",
    consultCtaMB: "Contact",
    consultBtnTop: { xs: "50%", md: "50%" },
    consultBtnleft: { xs: "17%", md: "22%" },
    brandLabel: "Brand",
    brandAll: "All brands",
  },
  JP: {
    backChip: "一つ上の階層",
    searchPlaceholder: "キーワードを入力して商品を絞り込む…",
    clear: "クリア",
    loading: "読み込み中...",
    loadError: "商品を読み込めませんでした",
    noProducts: "現在、条件に合う商品はありません。",
    pageTitleFallback: "商品情報",
    perPageLabel: (n) => `1ページ ${n} 件`,
    pageCaption: (page, totalPages, pageSize, max) =>
      `第 ${page} / 全 ${totalPages} ページ（1ページ ${pageSize} 件、最大 ${max} 件）`,
    consultTitle: "プロ相談",
    consultCta: "専門家による無料相談",
    consultCtaMB: "相談する",
    consultBtnTop: { xs: "50%", md: "50%" },
    consultBtnleft: { xs: "17%", md: "22%" },
    brandLabel: "ブランド",
    brandAll: "すべてのブランド",
  },
};

/* ---------------- NEW: user ID resolver (prevents disabled heart) ---------------- */

function resolveUserIdAny(user) {
  return resolveCanonicalUserID(user);
}

function CategoryScroller({
  items = [],
  activeHref,
  onSelect,
  onBack,
  login,
  lang = "EN",
  wrapperRef,
}) {
  const railRef = useRef(null);
  const t = UI_TEXT[lang] || UI_TEXT.ZH;

  const scrollBy = (dir = 1) => {
    const el = railRef.current;
    if (!el) return;
    const dx = Math.round(el.clientWidth * 0.8) * dir;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  const chipSX = (active) => ({
    height: { xs: 40, sm: 44, md: 48 },
    borderRadius: 999,
    boxShadow: 0,
    borderColor: active ? "primary.main" : "divider",
    fontWeight: active ? 800 : 600,
    "& .MuiChip-label": {
      px: { xs: 1.8, sm: 2.2, md: 2.6 },
      py: 0,
      fontSize: { xs: 14, sm: 15, md: 16 },
      fontWeight: active ? 800 : 600,
      whiteSpace: "nowrap",
    },
    "& .MuiChip-icon": {
      fontSize: { xs: 18, md: 20 },
      ml: 0.2,
      mr: 0.4,
    },
  });

  useEffect(() => {
    const el = railRef.current;
    if (!el || !activeHref) return;

    const chip = el.querySelector(`[data-href="${activeHref}"]`);
    if (!chip) return;

    try {
      chip.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    } catch {}
  }, [activeHref]);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: "relative",
        my: 1,
        "&:before, &:after": {
          content: '""',
          position: "absolute",
          top: 0,
          bottom: 0,
          width: 28,
          zIndex: 1,
          pointerEvents: "none",
          display: { xs: "none", sm: "block" },
        },
        "&:before": {
          left: 0,
          background:
            "linear-gradient(90deg, var(--mui-palette-background-default), rgba(255,255,255,0))",
        },
        "&:after": {
          right: 0,
          background:
            "linear-gradient(270deg, var(--mui-palette-background-default), rgba(255,255,255,0))",
        },
      }}
    >
      <IconButton
        onClick={() => scrollBy(-1)}
        sx={{
          position: "absolute",
          left: -8,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          width: { xs: 34, md: 40 },
          height: { xs: 34, md: 40 },
          display: { xs: "none", sm: "inline-flex" },
        }}
      >
        <ChevronLeftRounded />
      </IconButton>

      <Box
        ref={railRef}
        sx={{
          overflowX: "auto",
          scrollbarWidth: "thin",
          msOverflowStyle: "none",
          "::-webkit-scrollbar": { height: 8 },
          "::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,.2)",
            borderRadius: 999,
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            gap: { xs: 1, md: 1.25 },
            alignItems: "center",
            px: { xs: 1, sm: 5 },
            py: { xs: 0.5, md: 0.75 },
            minHeight: { xs: 60, md: 72 },
            scrollSnapType: "x mandatory",
            "& > *": { scrollSnapAlign: "center" },
          }}
        >
          {onBack && (
            <Chip
              label={t.backChip}
              onClick={onBack}
              icon={<ChevronLeftRounded />}
              variant="outlined"
              clickable
              sx={chipSX(false)}
            />
          )}

          {items.map((c) => {
            const normHref = c._normalized || c.href;
            const isActive = normHref === activeHref;
            const label = byLangNode(c, lang);
            return (
              <Chip
                key={c.href}
                data-href={normHref}
                label={label}
                disabled={c.login === "required" && login === false}
                clickable
                color={isActive ? "primary" : "default"}
                variant={isActive ? "filled" : "outlined"}
                onClick={() => onSelect?.(c.href)}
                sx={chipSX(isActive)}
              />
            );
          })}
        </Box>
      </Box>

      <IconButton
        onClick={() => scrollBy(1)}
        sx={{
          position: "absolute",
          right: -8,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          width: { xs: 34, md: 40 },
          height: { xs: 34, md: 40 },
          display: { xs: "none", sm: "inline-flex" },
        }}
      >
        <ChevronRightRounded />
      </IconButton>
    </Box>
  );
}

function upOneLevel(root, current) {
  if (!root || !current) return root;
  const crumbs = getAncestors(root, current);
  return crumbs.length > 1 ? crumbs[crumbs.length - 2] : root;
}

function pickTranslatedNameFromLegacyFields(raw, lang = "EN") {
  const L = String(lang || "ZH").toUpperCase();

  const mainEN =
    raw?.mainInfoTranslationEN ||
    raw?.maininfotranslationEN ||
    raw?.mainInfoTranslateEN ||
    raw?.maininfotranslateEN ||
    null;

  const mainJP =
    raw?.mainInfoTranslationJP ||
    raw?.maininfotranslationJP ||
    raw?.mainInfoTranslateJP ||
    raw?.maininfotranslateJP ||
    null;

  const mainZH =
    raw?.mainInfoTranslationZH ||
    raw?.maininfotranslationZH ||
    raw?.mainInfoTranslateZH ||
    raw?.maininfotranslateZH ||
    null;

  const readName = (obj) => {
    if (!obj) return "";
    const o = Array.isArray(obj) ? obj[0] : obj;
    const v =
      o?.name || o?.productName || o?.title || o?.label || o?.variantName || "";
    return String(v || "").trim();
  };

  const translated =
    L === "EN"
      ? readName(mainEN)
      : L === "JP"
        ? readName(mainJP)
        : readName(mainZH);

  if (translated) return translated;

  const fallback =
    raw?.name ||
    raw?.productName ||
    raw?.ProductName ||
    raw?.Name ||
    raw?.main?.name ||
    raw?.main?.productName ||
    raw?.main?.ProductName ||
    raw?.main?.Name ||
    "";

  return String(fallback || "").trim();
}

function pickTranslatedName(raw, lang = "EN") {
  const L = String(lang || "ZH").toUpperCase();

  const read = (obj) => {
    if (!obj) return "";
    const v =
      obj["產品名稱"] ||
      obj["商品名"] ||
      obj["商品名稱"] ||
      obj["商品系列名稱"] ||
      obj["name"] ||
      obj["productName"] ||
      obj["title"] ||
      obj["label"] ||
      "";
    return String(v || "").trim();
  };

  if (L === "EN") {
    const v =
      read(raw?.mainEN) ||
      read(raw?.mainInfoEN) ||
      read(raw?.mainInfoTranslationEN) ||
      "";
    if (v) return v;
  }

  if (L === "JP") {
    const v =
      read(raw?.mainJP) ||
      read(raw?.mainInfoJP) ||
      read(raw?.mainInfoTranslationJP) ||
      "";
    if (v) return v;
  }

  const zh =
    read(raw?.main) ||
    read(raw?.mainZH) ||
    read(raw?.mainInfo) ||
    read(raw?.mainInfoTranslationZH) ||
    "";
  if (zh) return zh;

  const legacy = pickTranslatedNameFromLegacyFields(raw, L);
  if (legacy) return legacy;

  const fallback =
    raw?.name ||
    raw?.productName ||
    raw?.ProductName ||
    raw?.main?.name ||
    raw?.main?.productName ||
    "";
  return String(fallback || "").trim();
}

function normalizeProductsForCards(data, lang = "EN") {
  const fallbackImg = "/img/empty.webp";

  return (Array.isArray(data) ? data : []).map((raw) => {
    const base = normalizeProductForCard(raw) || {};

    const firstImage =
      (raw?.images &&
        raw.images[0] &&
        (raw.images[0].url || raw.images[0].image)) ||
      "";

    const primaryImg =
      base.image ||
      base.imageUrl ||
      base.cover ||
      base.mainImage ||
      firstImage ||
      raw.image ||
      raw.imageUrl ||
      raw.cover ||
      raw.mainImage ||
      "";

    const safeImgRaw =
      primaryImg && primaryImg !== "null" && primaryImg !== "undefined"
        ? String(primaryImg).trim()
        : "";

    const safeImg = safeImgRaw ? resolveAssetUrl(safeImgRaw) : "";

    const localizedName = pickTranslatedName(raw, lang);

    const stableId =
      raw?.productID ||
      raw?.id ||
      raw?.main?.["編號"] ||
      raw?.main?.productID ||
      raw?.parentID ||
      raw?.mainInfoID ||
      raw?.sku ||
      "";

    const finalImg = safeImg || fallbackImg;

    return {
      ...raw,
      ...base,
      productID: stableId || raw?.productID,
      name: localizedName || base.name || raw.name,
      productName: localizedName || base.productName || raw.productName,
      image: finalImg,
      imageUrl: finalImg,
      mainImage: finalImg,
      cover: finalImg,
    };
  });
}

const getProductDisplayName = (p) =>
  (p?.parentID || p?.main?.variantName)?.toString();

const getUniqueConfigForProduct = (prod) => {
  const name = getProductDisplayName(prod);
  if (!name) return null;

  if (name.includes("灌膠")) {
    return {
      paragraphs: [
        "Hedgehog®系列產品提供種類繁多的衛生刷和掃帚。它們有多種形狀和尺寸，適用於多種用途。所有刷毛均採用PBT材質，具有不吸水、耐磨、耐酸鹼和良好彈性等優點，使Hedgehog®衛生產品高效耐用。",
        "該產品系列包括 Antifur® 樹脂固定刷和掃帚——專為食品飲料行業以及其他會與食品接觸的應用而設計。其防掉毛特性有助於確保刷毛不會進入食品或飲料。",
        "此外，Metaray® 還提供金屬可檢測系列產品，適用於要求嚴苛的食品和製藥生產環境。此系列產品包含可被金屬檢測設備檢測到的成分，從而防止此類物質殘留在最終產品中。",
        "所有 Hedgehog® 衛生刷和掃帚均採用符合人體工學的設計，握感舒適，使用方便。刷毛方向經過精心設計，可最大限度地提高清潔效率，並滿足其特定用途。符合人體工學的設計有助於預防因長期使用刷子而造成的職業傷害。",
        "刷毛的軟硬程度有多種選擇。硬毛刷和掃帚專為擦洗而設計，是去除頑固污漬的理想之選。軟毛刷和掃帚則非常適合清掃粉末和其他細小輕質顆粒。",
      ],
      image2: "",
    };
  }

  if (name.includes("金屬探測")) {
    return {
      paragraphs: [
        "Hedgehog® Metaray® 金屬可探測系列產品專為食品和製藥生產而設計，這些行業對清潔度和產品完整性有很高的要求。",
        "此系列產品採用特殊材料製成，其組件可在發生意外損壞導致碎片進入生產線時被金屬探測器檢測到。",
        "Hedgehog® Metaray® 金屬探測器系列符合 FDA 要求，可安全用於食品和藥品生產環境。",
        "我們提供多種產品供您選擇，旨在滿足各種不同的使用需求。",
      ],
      image2: "/img/products/B2B/hedgehog.webp",
    };
  }

  return null;
};

function getBrandName(p) {
  const v =
    p?.brandName ||
    p?.brand ||
    p?.Brand ||
    p?.main?.brand ||
    p?.main?.Brand ||
    p?.main?.brandName ||
    p?.BrandName ||
    p?.brand_name ||
    p?.main?.brand_name ||
    p?.main?.["品牌名稱"] ||
    p?.mainEN?.["品牌名稱"] ||
    p?.mainJP?.["品牌名稱"] ||
    "";

  let s = String(v || "").trim();

  const lower = s.toLowerCase();
  if (lower === "-" || lower.startsWith("-")) return "";
  if (lower === "brand") return "";

  return s;
}

const clearSavedStatesForThisPage = (pathname) => {
  try {
    const prefix = `b2b:ProductInfoPage:${pathname}:`;
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {}
};

function decodeRepeatedly(input, maxTimes = 3) {
  let s = String(input || "");
  for (let i = 0; i < maxTimes; i += 1) {
    try {
      const d = decodeURIComponent(s);
      if (d === s) break;
      s = d;
    } catch {
      break;
    }
  }
  return s;
}

function readSearchQ(locationSearch) {
  try {
    const params = new URLSearchParams(locationSearch || "");
    const raw = params.get("q") || "";
    const decoded = decodeRepeatedly(raw, 3);
    return String(decoded || "").trim();
  } catch {
    return "";
  }
}

function buildSelectedProductGroupFromQuery(groupID) {
  const id = decodeRepeatedly(groupID, 3).trim();

  if (!id) return null;

  const preview = getB2BGroupPreviewImage({
    group_id: id,
    groupID: id,
    key: id,
  });

  return {
    group_id: id,
    groupID: id,
    key: id,
    label: preview?.label || id,
    group_name: preview?.label || id,
    to: `/商品資訊/group/${encodeURIComponent(id)}`,
  };
}

export default function ProductInfoPageB2B() {
  const contentTopRef = useRef(null);
  const productListRef = useRef(null);

  const categoryBarRef = useRef(null);
  const chipsAnchorRef = useRef(null);

  const layoutWatchRef = useRef(null);
  const [layoutStableTick, setLayoutStableTick] = useState(0);
  const layoutStableRef = useRef(false);

  const scrollLockRef = useRef(false);
  const pendingPlanRef = useRef(null);

  const scrollToChipsAfterLoadRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();

  const { user } = useAuth();
  const isLoggedIn = !!user;

  // ✅ UPDATED: robust user id so ProductCard heart is not disabled
  const uid = useMemo(() => {
    return String(resolveUserIdAny(user) || "").trim();
  }, [user]);

  const [lang, setLang] = useState(getCurrentLang());
  const t = UI_TEXT[lang] || UI_TEXT.ZH;

  useEffect(() => {
    const onLangChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onLangChange);
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("app:languageChanged", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const consultCtaText =
    (isMobile ? t.consultCtaMB : t.consultCta) ||
    t.consultCta ||
    t.consultCtaMB;

  const sidebarConfig = ProductSideBarConfigB2B;

  const productInfoRoot = useMemo(() => {
    const direct = findProductInfoRoot(sidebarConfig);
    return (
      direct || {
        href: "/商品資訊",
        label: "商品資訊",
        EN: "Product Info",
        JP: "商品情報",
        children: [],
      }
    );
  }, [sidebarConfig]);

  const ROOT_HREF = useMemo(
    () => normalizeHref(productInfoRoot.href),
    [productInfoRoot],
  );

  const getComparableCategoryPath = (href) => {
    try {
      return decodeURI(String(href || ""))
        .replace(/^\/+/, "")
        .replace(/^b2b\//, "")
        .replace(/^b2c\//, "")
        .replace(/\/+$/, "");
    } catch {
      return String(href || "")
        .replace(/^\/+/, "")
        .replace(/^b2b\//, "")
        .replace(/^b2c\//, "")
        .replace(/\/+$/, "");
    }
  };

  const findNodeByComparableHref = (nodes, href) => {
    const target = getComparableCategoryPath(href);

    for (const node of nodes || []) {
      if (getComparableCategoryPath(node.href) === target) return node;

      const found = findNodeByComparableHref(node.children, href);
      if (found) return found;
    }

    return null;
  };

  const resolveCategoryHrefForUI = (href) => {
    const normalized = normalizeHref(href || ROOT_HREF);

    if (normalized === ROOT_HREF) return ROOT_HREF;

    const exactNode = findNodeByHref(productInfoRoot.children, normalized);
    if (exactNode?.href) return normalizeHref(exactNode.href);

    const comparableNode = findNodeByComparableHref(
      productInfoRoot.children,
      normalized,
    );

    return comparableNode?.href
      ? normalizeHref(comparableNode.href)
      : normalized;
  };

  const [currentHref, setCurrentHref] = useState(ROOT_HREF);

  const getHeaderOffsetPx = () => 120;

  const scrollToCategoryChipsReliable = () => {
    const anchor = chipsAnchorRef.current;
    if (!anchor) return;

    scrollLockRef.current = true;

    const headerOffset = getHeaderOffsetPx();
    const MAX_TRIES = 14;
    const TOL = 3;

    const attempt = (i) => {
      try {
        const rect = anchor.getBoundingClientRect();
        const targetTop = window.pageYOffset + rect.top - headerOffset;

        window.scrollTo({
          top: targetTop,
          left: 0,
          behavior: "auto",
        });
      } catch {}

      requestAnimationFrame(() => {
        const rect = anchor.getBoundingClientRect();
        const desiredTop = headerOffset;
        const delta = rect.top - desiredTop;

        if (Math.abs(delta) <= TOL || i >= MAX_TRIES) {
          scrollLockRef.current = false;
          return;
        }

        window.scrollBy({ top: delta, left: 0, behavior: "auto" });
        window.setTimeout(() => attempt(i + 1), 90);
      });
    };

    attempt(0);
  };

  const revealActiveChip = (activeHrefNorm) => {
    const wrap = categoryBarRef.current;
    if (!wrap || !activeHrefNorm) return;
    const chip = wrap.querySelector(`[data-href="${activeHrefNorm}"]`);
    if (!chip) return;
    try {
      chip.scrollIntoView({
        behavior: "auto",
        inline: "center",
        block: "nearest",
      });
    } catch {}
  };

  const jumpToPageTopInstantly = () => {
    manualTopScrollRef.current = true;
    restorePendingRef.current = false;
    savedScrollYRef.current = null;
    pendingPlanRef.current = null;
    scrollToChipsAfterLoadRef.current = false;

    const scrollTop = () => {
      try {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      } catch {}
    };

    scrollTop();
    requestAnimationFrame(scrollTop);
    window.setTimeout(scrollTop, 80);
    window.setTimeout(scrollTop, 220);
  };
  useLayoutEffect(() => {
    let prev = "auto";
    try {
      prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
    } catch {}
    return () => {
      try {
        window.history.scrollRestoration = prev;
      } catch {}
    };
  }, []);

  useEffect(() => {
    const el = layoutWatchRef.current;
    if (!el) return;

    let tt = 0;
    layoutStableRef.current = false;

    const markUnstable = () => {
      layoutStableRef.current = false;
      if (tt) window.clearTimeout(tt);

      tt = window.setTimeout(() => {
        layoutStableRef.current = true;
        setLayoutStableTick((x) => x + 1);
      }, 280);
    };

    markUnstable();

    let ro;
    try {
      ro = new ResizeObserver(() => markUnstable());
      ro.observe(el);
    } catch {
      tt = window.setTimeout(() => {
        layoutStableRef.current = true;
        setLayoutStableTick((x) => x + 1);
      }, 450);
    }

    const onResize = () => markUnstable();
    window.addEventListener("resize", onResize);

    return () => {
      if (tt) window.clearTimeout(tt);
      window.removeEventListener("resize", onResize);
      try {
        if (ro) ro.disconnect();
      } catch {}
    };
  }, []);

  const isFreshEntryFromNavbarMemo = useMemo(
    () => !!location?.state?.freshEntry,
    [location],
  );

  const sidebarKeyPrefix = useMemo(
    () => `b2b:ProductInfoPage:${location.pathname}:`,
    [location.pathname],
  );

  const storageKey = useMemo(() => {
    const cat = normalizeHref(currentHref || ROOT_HREF) || ROOT_HREF;
    return `${sidebarKeyPrefix}cat=${cat}`;
  }, [sidebarKeyPrefix, currentHref, ROOT_HREF]);

  const savedScrollYRef = useRef(null);
  const restorePendingRef = useRef(false);
  const manualTopScrollRef = useRef(false);

  const PAGE_MAX = 100;
  const [pageSize, setPageSize] = useState(8);
  const pageSizeClamped = Math.min(pageSize, PAGE_MAX);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid");
  const [brand, setBrand] = useState("__ALL__");

  const saveStateToSession = (extra = {}) => {
    try {
      const payload = {
        page,
        pageSize: pageSizeClamped,
        view,
        brand,
        search,
        scrollY: typeof window !== "undefined" ? window.scrollY || 0 : 0,
        ts: Date.now(),
        ...extra,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {}
  };

  const markReturnToChips = () => {
    try {
      sessionStorage.setItem(`${storageKey}:returnToChips`, "1");
    } catch {}
  };

  useLayoutEffect(() => {
    if (!isFreshEntryFromNavbarMemo) return;

    scrollToChipsAfterLoadRef.current = false;
    restorePendingRef.current = false;
    savedScrollYRef.current = null;
    pendingPlanRef.current = null;

    clearSavedStatesForThisPage(location.pathname);

    setCurrentHref(ROOT_HREF);
    setSearch("");
    setBrand("__ALL__");
    setView("grid");
    setPageSize(8);
    setPage(1);

    try {
      window.scrollTo(0, 0);
    } catch {}

    navigate(
      { pathname: location.pathname, search: "" },
      { replace: true, state: {} },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFreshEntryFromNavbarMemo, ROOT_HREF, location.pathname, navigate]);

  useEffect(() => {
    if (isFreshEntryFromNavbarMemo) return;

    const params = new URLSearchParams(location.search);
    const groupFromQuery = params.get("group");
    const catFromQuery = params.get("cat");
    const catFromState = location.state?.categoryHref;

    if (groupFromQuery) {
      const nextGroup = buildSelectedProductGroupFromQuery(groupFromQuery);

      setSelectedProductGroup(nextGroup);
      setCurrentHref(ROOT_HREF);
      scrollToChipsAfterLoadRef.current = false;
      restorePendingRef.current = false;
      savedScrollYRef.current = null;
      return;
    }

    if (catFromQuery) {
      const decoded = decodeRepeatedly(catFromQuery, 3);
      const next = resolveCategoryHrefForUI(decoded);

      setSelectedProductGroup(null);
      setCurrentHref(next);
      scrollToChipsAfterLoadRef.current = false;
      return;
    }

    if (catFromState) {
      const target = resolveCategoryHrefForUI(catFromState);
      setCurrentHref(target);
      scrollToChipsAfterLoadRef.current = false;
      jumpToPageTopInstantly();

      const basePath = location.pathname;

      try {
        window.history.replaceState(
          { ...window.history.state, categoryHref: undefined },
          "",
          basePath,
        );
      } catch {}

      navigate(
        {
          pathname: basePath,
          search: `?cat=${encodeURIComponent(target)}`,
        },
        { replace: false },
      );
      return;
    }

    setSelectedProductGroup(null);
    setCurrentHref(ROOT_HREF);
  }, [
    isFreshEntryFromNavbarMemo,
    location.pathname,
    location.search,
    location.state,
    ROOT_HREF,
    navigate,
  ]);

  const urlQ = useMemo(() => readSearchQ(location.search), [location.search]);
  const hasUrlQ = !!urlQ;

  useEffect(() => {
    if (isFreshEntryFromNavbarMemo) return;
    if (!hasUrlQ) return;

    const params = new URLSearchParams(location.search || "");
    const hasCat = !!params.get("cat");

    setSearch(urlQ);
    setBrand("__ALL__");
    setPage(1);

    if (!hasCat) {
      setCurrentHref(ROOT_HREF);
      scrollToChipsAfterLoadRef.current = false;

      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch {}
    }
  }, [hasUrlQ, urlQ, ROOT_HREF, location.search, isFreshEntryFromNavbarMemo]);

  useEffect(() => {
    const normalized = normalizeHref(currentHref);
    const exists =
      normalized === ROOT_HREF ||
      !!findNodeByHref(productInfoRoot.children, normalized);
    if (!exists) setCurrentHref(ROOT_HREF);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productInfoRoot]);

  const currentNode = useMemo(() => {
    if (!productInfoRoot) return null;
    if (normalizeHref(currentHref) === ROOT_HREF) return productInfoRoot;
    return (
      findNodeByHref(productInfoRoot.children, currentHref) || productInfoRoot
    );
  }, [currentHref, productInfoRoot, ROOT_HREF]);

  const isConsultPage =
    currentNode?.label?.includes("專業諮詢") ||
    currentNode?.label?.includes("專人服務");

  const crumbs = useMemo(() => {
    if (!productInfoRoot || !currentNode) return [];
    return getAncestors(productInfoRoot, currentNode);
  }, [productInfoRoot, currentNode]);

  const [products, setProducts] = useState([]);
  const [selectedProductGroup, setSelectedProductGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;

    async function fetchByCategory() {
      setLoading(true);
      setError("");

      try {
        const normalizedHref =
          normalizeHref(currentHref || ROOT_HREF) || ROOT_HREF;

        let data = [];

        if (selectedProductGroup?.group_id) {
          data = await ProductsAPI.byProductGroup({
            groupID: selectedProductGroup.group_id,
            lang,
            userID: uid,
          });
        } else {
          data = await ProductsAPI.byCategory({
            code: normalizedHref,
            lang,
            mode: "b2b",
            userID: uid,
          });
        }

        if (!ignore) {
          setProducts(normalizeProductsForCards(data, lang));
        }
      } catch (err) {
        console.error("Failed to load products:", err);
        if (!ignore) {
          setError(t.loadError);
          setProducts([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    fetchByCategory();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHref, ROOT_HREF, selectedProductGroup, lang, uid]);

  useEffect(() => setPage(1), [search, brand]);
  useEffect(() => setPage(1), [currentHref, view, pageSizeClamped, brand]);

  const brandOptions = useMemo(() => {
    const BRAND_ORDER = [
      "SEHO",
      "電潔水",
      "擦拭君",
      "saraya",
      "dyson",
      "hedgehog",
    ];

    const EXCLUDED_BRANDS = ["mizuka"];

    const set = new Set();

    for (const p of products) {
      const b = getBrandName(p);

      if (!b) continue;

      const normalized = String(b).trim();

      if (
        EXCLUDED_BRANDS.some(
          (x) => x.toLowerCase() === normalized.toLowerCase(),
        )
      ) {
        continue;
      }

      set.add(normalized);
    }

    const existingBrands = Array.from(set);

    return BRAND_ORDER.filter((brand) =>
      existingBrands.some((b) => b.toLowerCase() === brand.toLowerCase()),
    );
  }, [products]);

  const filteredBySearchAndBrand = useMemo(() => {
    const q = search.trim().toLowerCase();
    const source = products;

    const byBrand =
      brand === "__ALL__"
        ? source
        : source.filter((p) => getBrandName(p) === brand);

    if (!q) return byBrand;

    const getSearchText = (p) => {
      const baseName = p?.name || p?.productName || "";
      const variantName =
        p?.variantName ||
        p?.main?.variantName ||
        p?.main?.["產品變體名稱"] ||
        "";

      const brandName = getBrandName(p);

      return `${brandName} ${baseName} ${variantName}`.toString().trim();
    };

    return byBrand.filter((p) => getSearchText(p).toLowerCase().includes(q));
  }, [products, search, brand]);

  const sortedProducts = useMemo(
    () => [...filteredBySearchAndBrand],
    [filteredBySearchAndBrand],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(sortedProducts.length / pageSizeClamped),
  );

  const pagedProducts = useMemo(() => {
    const start = (page - 1) * pageSizeClamped;
    return sortedProducts.slice(start, start + pageSizeClamped);
  }, [sortedProducts, page, pageSizeClamped]);

  useEffect(() => {
    if (isFreshEntryFromNavbarMemo) return;

    if (hasUrlQ) {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        const st = JSON.parse(raw);

        if (typeof st?.pageSize === "number") setPageSize(st.pageSize);
        if (st?.view === "grid" || st?.view === "list") setView(st.view);
      } catch {}
      return;
    }

    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const st = JSON.parse(raw);

      if (typeof st?.pageSize === "number") setPageSize(st.pageSize);
      if (st?.view === "grid" || st?.view === "list") setView(st.view);
      if (typeof st?.page === "number") setPage(st.page);
      if (typeof st?.search === "string") setSearch(st.search);
      if (typeof st?.brand === "string") setBrand(st.brand);

      if (typeof st?.scrollY === "number") {
        savedScrollYRef.current = st.scrollY;
        restorePendingRef.current = true;
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, isFreshEntryFromNavbarMemo, hasUrlQ]);

  useEffect(() => {
    if (isFreshEntryFromNavbarMemo) return;
    saveStateToSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSizeClamped,
    view,
    brand,
    search,
    storageKey,
    isFreshEntryFromNavbarMemo,
  ]);

  useEffect(() => {
    if (isFreshEntryFromNavbarMemo) return;

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        saveStateToSession();
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storageKey,
    page,
    pageSizeClamped,
    view,
    brand,
    search,
    isFreshEntryFromNavbarMemo,
  ]);

  useEffect(() => {
    if (isFreshEntryFromNavbarMemo) return;
    if (loading) return;
    if (isConsultPage) {
      scrollToChipsAfterLoadRef.current = false;
      return;
    }
    if (!scrollToChipsAfterLoadRef.current) return;

    const active = normalizeHref(currentHref || ROOT_HREF) || ROOT_HREF;
    pendingPlanRef.current = { type: "chips", active };
    scrollToChipsAfterLoadRef.current = false;
  }, [
    loading,
    isConsultPage,
    currentHref,
    ROOT_HREF,
    isFreshEntryFromNavbarMemo,
  ]);

  useLayoutEffect(() => {
    if (isFreshEntryFromNavbarMemo) return;
    if (loading) return;
    if (isConsultPage) return;
    if (!layoutStableRef.current) return;

    const plan = pendingPlanRef.current;
    if (!plan || plan.type !== "chips") return;

    pendingPlanRef.current = null;

    const active = plan.active;

    scrollToCategoryChipsReliable("auto");
    revealActiveChip(active);

    window.setTimeout(() => {
      scrollToCategoryChipsReliable("auto");
      revealActiveChip(active);
    }, 480);
  }, [layoutStableTick, loading, isConsultPage, isFreshEntryFromNavbarMemo]);

  useEffect(() => {
    if (isFreshEntryFromNavbarMemo) return;
    if (loading) return;
    if (isConsultPage) return;
    if (scrollLockRef.current) return;
    if (pendingPlanRef.current) return;

    if (manualTopScrollRef.current) {
      restorePendingRef.current = false;
      savedScrollYRef.current = null;
      requestAnimationFrame(() => {
        try {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        } catch {}
      });
      return;
    }

    if (!restorePendingRef.current) return;

    const y = savedScrollYRef.current;
    if (typeof y !== "number") {
      restorePendingRef.current = false;
      return;
    }

    requestAnimationFrame(() => {
      window.scrollTo({ top: y, left: 0, behavior: "auto" });
      restorePendingRef.current = false;
    });
  }, [loading, isConsultPage, isFreshEntryFromNavbarMemo]);

  useEffect(() => {
    if (!manualTopScrollRef.current) return;
    if (loading) return;

    const clearManualTop = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch {}
      manualTopScrollRef.current = false;
    };

    requestAnimationFrame(clearManualTop);
    window.setTimeout(clearManualTop, 120);
  }, [loading]);

  const handleSelectCategory = (categoryHref) => {
    setSelectedProductGroup(null);
    const target = resolveCategoryHrefForUI(categoryHref);
    setCurrentHref(target);
    setSearch("");
    setBrand("__ALL__");
    setPage(1);

    try {
      const params = new URLSearchParams(location.search);
      const hadCatBefore = !!params.get("cat");

      const rootNorm = normalizeHref(productInfoRoot.href);
      const isRootTarget = !target || target === rootNorm;

      const nextParams = new URLSearchParams(location.search);

      if (isRootTarget) {
        nextParams.delete("cat");
      } else {
        nextParams.set("cat", target);
      }

      nextParams.delete("q");

      const searchString = nextParams.toString();
      const nextSearch = searchString ? `?${searchString}` : "";

      const shouldPushNewEntry = !hadCatBefore && !isRootTarget;

      navigate(
        {
          pathname: location.pathname,
          search: nextSearch,
        },
        { replace: !shouldPushNewEntry },
      );
    } catch (e) {
      console.error("Failed to update category in URL:", e);
    }

    scrollToChipsAfterLoadRef.current = false;
    pendingPlanRef.current = null;
    restorePendingRef.current = false;
    savedScrollYRef.current = null;
    jumpToPageTopInstantly();
  };

  const handleSelectProductGroup = (group) => {
    const nextGroup = group || null;

    setSelectedProductGroup(nextGroup);
    setCurrentHref(ROOT_HREF);
    setSearch("");
    setBrand("__ALL__");
    setPage(1);

    try {
      const nextParams = new URLSearchParams(location.search);

      if (nextGroup?.group_id) {
        nextParams.delete("cat");
        nextParams.delete("q");
        nextParams.set("group", String(nextGroup.group_id));
      } else {
        nextParams.delete("group");
      }

      const searchString = nextParams.toString();

      navigate(
        {
          pathname: location.pathname,
          search: searchString ? `?${searchString}` : "",
        },
        { replace: false },
      );
    } catch (e) {
      console.error("Failed to update product group in URL:", e);
    }

    scrollToChipsAfterLoadRef.current = false;
    pendingPlanRef.current = null;
    restorePendingRef.current = false;
    savedScrollYRef.current = null;
    jumpToPageTopInstantly();
  };

  useEffect(() => {
    const onPicked = (e) => {
      const to = e?.detail?.to;
      if (!to) return;
      const target = normalizeHref(to);
      handleSelectCategory(target);
    };

    window.addEventListener("app:categorySelected", onPicked);
    return () => window.removeEventListener("app:categorySelected", onPicked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, productInfoRoot?.href]);

  const pageTitle =
    selectedProductGroup?.group_name ||
    selectedProductGroup?.label ||
    byLangNode(currentNode, lang) ||
    t.pageTitleFallback;

  const buildNavForProduct = (prod) => {
    const snapshotBeforeLeave = () => {
      saveStateToSession();
      markReturnToChips();
    };

    const uniqueCfg = getUniqueConfigForProduct(prod);

    if (uniqueCfg) {
      const path = "/商品資訊/unique";
      const title = getProductDisplayName(prod);

      const rawPrimaryImage =
        prod?.mainImage ||
        prod?.image ||
        prod?.imageUrl ||
        (Array.isArray(prod?.images) &&
          prod.images[0] &&
          (prod.images[0].url || prod.images[0].image)) ||
        "/img/empty.webp";

      const primaryImage =
        rawPrimaryImage === "/img/empty.webp"
          ? rawPrimaryImage
          : resolveAssetUrl(rawPrimaryImage);

      const onClick = (e) => {
        if (e && e.metaKey) return;
        if (e && e.ctrlKey) return;
        if (e && e.shiftKey) return;
        if (e && e.button === 1) return;

        if (e && e.preventDefault) e.preventDefault();

        snapshotBeforeLeave();

        navigate(path, {
          state: {
            ...uniqueCfg,
            title,
            image: primaryImage,
            fromProductID: prod?.productID,
            fromBreadcrumbs: crumbs.map((c) => ({
              label: byLangNode(c, lang),
              href: normalizeHref(c.href),
            })),
            fromCategoryHref: normalizeHref(currentHref),
            fromListKey: storageKey,
          },
        });
      };

      return { to: path, onClick };
    }

    const detailPath = buildB2BDetailPath(prod);
    if (!detailPath) return { to: undefined, onClick: undefined };

    return {
      to: detailPath,
      onClick: (e) => {
        if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1))
          return;
        if (e && e.preventDefault) e.preventDefault();

        snapshotBeforeLeave();

        const breadcrumbState = crumbs.map((c) => ({
          label: byLangNode(c, lang),
          href: normalizeHref(c.href),
        }));

        navigate(detailPath, {
          state: {
            fromBreadcrumbs: breadcrumbState,
            fromCategoryHref: normalizeHref(currentHref),
            fromListKey: storageKey,
          },
        });
      },
    };
  };

  const childrenOfCurrent = (currentNode?.children || []).map((c) => ({
    ...c,
    _normalized: normalizeHref(c.href),
  }));

  const parentNode = upOneLevel(productInfoRoot, currentNode);
  const activeHrefNormalized = normalizeHref(currentHref);

  const isCategorySelected =
    activeHrefNormalized && activeHrefNormalized !== ROOT_HREF;

  const selectedCategoryPreview = isCategorySelected
    ? getB2BCategoryPreviewImage(activeHrefNormalized)
    : null;

  const isProductGroupSelected = !!selectedProductGroup?.group_id;
  const selectedProductGroupPreview = isProductGroupSelected
    ? getB2BGroupPreviewImage(selectedProductGroup)
    : null;

  const selectedHeroPreview = selectedProductGroupPreview?.img
    ? selectedProductGroupPreview
    : selectedCategoryPreview?.img
      ? selectedCategoryPreview
      : null;
  const isSelectedProductLayout = isCategorySelected || isProductGroupSelected;

  const displayCrumbs = isProductGroupSelected
    ? [
        productInfoRoot,
        {
          href: selectedProductGroup?.to || "",
          label:
            selectedProductGroup?.group_name ||
            selectedProductGroup?.label ||
            pageTitle,
        },
      ]
    : crumbs;
  const consultHref = useMemo(() => {
    const fallback = "/商品資訊/專業諮詢";
    const root = productInfoRoot;
    if (!root) return fallback;
    const queue = [...(root.children || [])];
    while (queue.length) {
      const node = queue.shift();
      const label = (node?.label || "").toString();
      if (label.includes("專業諮詢") || label.includes("專人服務")) {
        return node.href || fallback;
      }
      if (node.children?.length) queue.push(...node.children);
    }
    return fallback;
  }, [productInfoRoot]);

  const handleGoConsult = () => {
    if (!consultHref) return;
    handleSelectCategory(consultHref);
  };

  // ✅ Keep product list in sync when user toggles favourite
  const handleFavouriteChange = (next, productID) => {
    const pid = String(productID || "").trim();
    if (!pid) return;

    setProducts((prev) =>
      (Array.isArray(prev) ? prev : []).map((p) => {
        const id = String(
          p?.productID || p?.id || p?.main?.["編號"] || "",
        ).trim();
        if (id !== pid) return p;
        return { ...p, favourite: next ? 1 : 0 };
      }),
    );
  };

  if (isConsultPage) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#fffffc" }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 1080,
            mx: "auto",
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              mb: 2,
              overflowX: "auto",
              whiteSpace: "nowrap",
              "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
            }}
          >
            <Breadcrumbs aria-label="breadcrumb">
              {crumbs.map((n, i) => {
                const isLast = i === crumbs.length - 1;
                const to = normalizeHref(n.href);
                const label = byLangNode(n, lang);
                return isLast ? (
                  <Typography key={to} color="text.primary">
                    {label}
                  </Typography>
                ) : (
                  <MLink
                    key={to}
                    underline="hover"
                    color="inherit"
                    component="button"
                    onClick={() => handleSelectCategory(to)}
                  >
                    {label}
                  </MLink>
                );
              })}
            </Breadcrumbs>
          </Box>

          <ProductConsultForm mode="b2b" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fffffc" }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1080,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
        }}
      >
        {/* {!isCategorySelected && <ServiceAdvantages opacity={1} />} */}

        {isSelectedProductLayout && selectedHeroPreview?.img && (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              mb: { xs: 3, md: 5 },
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={selectedHeroPreview.img}
              alt={selectedHeroPreview.label || pageTitle || ""}
              sx={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: selectedHeroPreview.objectfit || "cover",
                objectPosition: selectedHeroPreview.imgPosition || "center",
              }}
            />

            {/* {selectedCategoryPreview?.type === "area" && ( */}
            {!!selectedHeroPreview?.img && (
              <Box
                sx={{
                  position: "absolute",
                  left: { xs: 10, sm: 16 },
                  bottom: { xs: 10, sm: 16 },
                  bgcolor: "#fff",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.75, sm: 1 },
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  maxWidth: "calc(100% - 32px)",
                }}
              >
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: font[lang],
                    fontSize: { xs: 20, sm: 24, md: 28 },
                    fontWeight: 400,
                    lineHeight: 1.2,
                    color: "text.primary",
                  }}
                >
                  {pageTitle}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {!isSelectedProductLayout && (
          <Box>
            <ProductIntroTopSection
              onSelectCategory={handleSelectCategory}
              currentHref={currentHref}
              isLoggedIn={isLoggedIn}
            />

            <CategoryGroup
              currentHref={currentHref}
              activeGroupID={selectedProductGroup?.group_id || ""}
              onSelectCategory={handleSelectCategory}
              onSelectProductGroup={handleSelectProductGroup}
            />
          </Box>
        )}

        <Box
          ref={chipsAnchorRef}
          sx={{
            scrollMarginTop: "120px",
            height: 0,
          }}
        />

        <CategoryScroller
          wrapperRef={categoryBarRef}
          items={childrenOfCurrent}
          activeHref={activeHrefNormalized}
          onSelect={handleSelectCategory}
          login={isLoggedIn}
          lang={lang}
          onBack={
            currentNode && normalizeHref(currentNode.href) !== ROOT_HREF
              ? () => handleSelectCategory(parentNode?.href || ROOT_HREF)
              : undefined
          }
        />

        <Box
          sx={{
            mb: 2,
            overflowX: "auto",
            whiteSpace: "nowrap",
            "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
          }}
        >
          <Breadcrumbs aria-label="breadcrumb">
            {displayCrumbs.map((n, i) => {
              const isLast = i === displayCrumbs.length - 1;
              const to = normalizeHref(n.href);
              const label = byLangNode(n, lang);
              return isLast ? (
                <Typography key={to} color="text.primary">
                  {label}
                </Typography>
              ) : (
                <MLink
                  key={to}
                  underline="hover"
                  color="inherit"
                  component="button"
                  onClick={() => handleSelectCategory(to)}
                >
                  {label}
                </MLink>
              );
            })}
          </Breadcrumbs>
        </Box>

        <Box ref={layoutWatchRef}>
          {loading && (
            <Box
              sx={{
                p: 4,
                minHeight: 150,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SehoLoadingSpinner />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ p: 4, textAlign: "center", color: "error.main" }}>
              {error}
            </Box>
          )}

          {!loading && !error && (
            <>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1.25}
                sx={{ mb: 2 }}
              >
                {!(
                  // selectedCategoryPreview?.type === "area" &&
                  selectedHeroPreview?.img
                ) && (
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {pageTitle}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    gap: 1.25,
                    flexWrap: "nowrap",
                  }}
                >
                  <TextField
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t.searchPlaceholder}
                    sx={{ minWidth: 260 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: search ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearch("")}
                            aria-label="clear search"
                            edge="end"
                          >
                            ✕
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />

                  <ToggleButtonGroup
                    exclusive
                    size="medium"
                    value={view}
                    onChange={(_, v) => v && setView(v)}
                  >
                    <ToggleButton value="grid">
                      <ViewModuleIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="list">
                      <ViewListIcon fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                      value={pageSizeClamped}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      <MenuItem value={8}>{t.perPageLabel(8)}</MenuItem>
                      <MenuItem value={20}>{t.perPageLabel(20)}</MenuItem>
                      <MenuItem value={40}>{t.perPageLabel(40)}</MenuItem>
                      <MenuItem value={60}>{t.perPageLabel(60)}</MenuItem>
                      <MenuItem value={80}>{t.perPageLabel(80)}</MenuItem>
                      <MenuItem value={100}>{t.perPageLabel(100)}</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={brand}
                      onChange={(e) => setBrand(String(e.target.value))}
                      displayEmpty
                      renderValue={(v) => {
                        if (v === "__ALL__") return `${t.brandAll}`;
                        return `${v}`;
                      }}
                    >
                      <MenuItem value="__ALL__">{t.brandAll}</MenuItem>
                      {brandOptions.map((b) => (
                        <MenuItem key={b} value={b}>
                          {b}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box
                  sx={{ display: { xs: "block", sm: "none" }, width: "100%" }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t.searchPlaceholder}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: search ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearch("")}
                            aria-label="clear search"
                            edge="end"
                          >
                            ✕
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      WebkitOverflowScrolling: "touch",
                      pb: 0.25,
                      "&::-webkit-scrollbar": { height: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        background: "rgba(0,0,0,.18)",
                        borderRadius: 999,
                      },
                    }}
                  >
                    <ToggleButtonGroup
                      exclusive
                      size="medium"
                      value={view}
                      onChange={(_, v) => v && setView(v)}
                      sx={{ flex: "0 0 auto" }}
                    >
                      <ToggleButton value="grid">
                        <ViewModuleIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButton value="list">
                        <ViewListIcon fontSize="small" />
                      </ToggleButton>
                    </ToggleButtonGroup>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 100, flex: "0 0 auto" }}
                    >
                      <Select
                        value={pageSizeClamped}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                      >
                        <MenuItem value={8}>{t.perPageLabel(8)}</MenuItem>
                        <MenuItem value={20}>{t.perPageLabel(20)}</MenuItem>
                        <MenuItem value={40}>{t.perPageLabel(40)}</MenuItem>
                        <MenuItem value={60}>{t.perPageLabel(60)}</MenuItem>
                        <MenuItem value={80}>{t.perPageLabel(80)}</MenuItem>
                        <MenuItem value={100}>{t.perPageLabel(100)}</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 100, flex: "0 0 auto" }}
                    >
                      <Select
                        value={brand}
                        onChange={(e) => setBrand(String(e.target.value))}
                        displayEmpty
                        renderValue={(v) => {
                          if (v === "__ALL__") return `${t.brandAll}`;
                          return `${v}`;
                        }}
                      >
                        <MenuItem value="__ALL__">{t.brandAll}</MenuItem>
                        {brandOptions.map((b) => (
                          <MenuItem key={b} value={b}>
                            {b}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Stack>

              <div ref={productListRef}>
                {view === "grid" ? (
                  <Box
                    sx={{
                      display: "grid",
                      gap: { xs: 2, sm: 3, md: 4 },
                      gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: "repeat(3, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                      },
                    }}
                  >
                    {pagedProducts.map((p) => {
                      const { to, onClick } = buildNavForProduct(p);
                      return (
                        <ProductCard
                          key={p.productID || p.parentID || p.id}
                          product={p}
                          to={to}
                          view="grid"
                          titleLines={2}
                          onClickCard={onClick}
                          showFavourite={true}
                          userID={uid}
                          onFavouriteChange={handleFavouriteChange}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Stack spacing={1.5}>
                    {pagedProducts.map((p) => {
                      const { to, onClick } = buildNavForProduct(p);
                      return (
                        <ProductCard
                          key={p.productID || p.parentID || p.id}
                          product={p}
                          to={to}
                          view="list"
                          onClickCard={onClick}
                          showFavourite={true}
                          userID={uid}
                          onFavouriteChange={handleFavouriteChange}
                        />
                      );
                    })}
                  </Stack>
                )}
              </div>

              {sortedProducts.length === 0 && (
                <Box
                  sx={{ p: 4, textAlign: "center", color: "text.secondary" }}
                >
                  {t.noProducts}
                </Box>
              )}

              {sortedProducts.length > 0 && (
                <Stack alignItems="center" sx={{ mt: 3 }}>
                  <Pagination
                    page={page}
                    count={totalPages}
                    onChange={(_, v) => setPage(v)}
                    shape="rounded"
                    color="primary"
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      color: "text.secondary",
                      textAlign: "center",
                    }}
                  >
                    {t.pageCaption(page, totalPages, pageSizeClamped, PAGE_MAX)}
                  </Typography>
                </Stack>
              )}
            </>
          )}

          {isSelectedProductLayout && (
            <>
              <Box sx={{ mt: { xs: 5, md: 7 } }}>
                <CategoryGroup
                  currentHref={currentHref}
                  activeGroupID={selectedProductGroup?.group_id || ""}
                  onSelectCategory={handleSelectCategory}
                  onSelectProductGroup={handleSelectProductGroup}
                />
              </Box>
              <Box sx={{ mt: { xs: 5, md: 7 } }}>
                <ProductIntroTopSection
                  onSelectCategory={handleSelectCategory}
                  currentHref={currentHref}
                  isLoggedIn={isLoggedIn}
                  cardsOnly
                  forceSectionKey={selectedCategoryPreview?.type || ""}
                />
              </Box>
            </>
          )}

          {/* <ServiceProcess /> */}

          <Box
            sx={{
              mt: { xs: 4, md: 5 },
              mb: { xs: 2, md: 3 },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: 3,
                maxWidth: 1080,
                width: "100%",
                cursor: "default",
              }}
            >
              <Box
                component="img"
                src="/img/products/B2B/productConsult.webp"
                alt={t.consultTitle}
                draggable={false}
                sx={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  top: t.consultBtnTop,
                  left: t.consultBtnleft,
                  transform: "translate(-50%, -50%)",
                  px: { xs: 0, md: 5 },
                  textAlign: { xs: "center", md: "center" },
                  borderRadius: 2,
                  py: { xs: 2, md: 3 },
                  pointerEvents: "auto",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: getPrimaryColor("b2b"),
                    pointerEvents: "none",
                    fontWeight: 700,
                    fontSize: { xs: 20, md: 35, lg: 35 },
                    fontFamily: font[lang],
                    whiteSpace: "pre-line",
                    mb: 0.5,
                  }}
                >
                  {t.consultTitle}
                </Typography>

                <Box
                  sx={{
                    mt: 1,
                    width: { xs: "75%", md: "100%" },
                    mx: "auto",
                    pointerEvents: "auto",
                  }}
                >
                  <ArrowButton
                    label={consultCtaText}
                    onClick={handleGoConsult}
                    fontFamily={font[lang]}
                    bgColor="#FFFFFF"
                    textColor={getPrimaryColor("b2b")}
                    hoverBgColor={getPrimaryColor("b2b")}
                    hoverTextColor="#FFFFFF"
                    arrowColor={getPrimaryColor("b2b")}
                    hoverArrowColor="#FFFFFF"
                    borderColor={getPrimaryColor("b2b")}
                    borderRadius={3}
                    minHeight={{ xs: 36, md: 48, lg: 56 }}
                    px={{ xs: "14px", md: "18px", lg: "22px" }}
                    fontSize={{ xs: "10px", md: "15px", lg: "20px" }}
                    fontWeight={400}
                    textPr={{ xs: "18px", md: "24px", lg: "28px" }}
                    arrowRight={{ xs: "10px", md: "16px", lg: "18px" }}
                    arrowSize={{ xs: "14px", md: "16px", lg: "18px" }}
                    width="100%"
                    sx={{
                      boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
